import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import { AdminRealtimeOverview, AdminRealtimeSeriesPoint } from '../types';

const EMPTY_SERIES: AdminRealtimeSeriesPoint[] = [];

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
};

const getSeriesMax = (series: AdminRealtimeSeriesPoint[], accessor: (point: AdminRealtimeSeriesPoint) => number): number => {
  const values = series.map(accessor);
  return values.length ? Math.max(...values, 0) : 0;
};

const getSeriesHeight = (value: number, max: number): number => {
  if (max <= 0 || value <= 0) return 8;
  return Math.max(Math.round((value / max) * 100), 12);
};

const RealtimePage: React.FC = () => {
  const [payload, setPayload] = useState<AdminRealtimeOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPayload(await adminService.getRealtimeOverview());
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar o realtime administrativo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const summary = payload?.summary;
  const series = payload?.series || EMPTY_SERIES;

  const metricCards = useMemo(
    () =>
      summary
        ? [
            { eyebrow: 'Realtime', title: 'Jogadores online', value: String(summary.online_players_now), tone: 'emerald' as const },
            { eyebrow: 'Sessoes', title: 'Ativas agora', value: String(summary.active_sessions_now), tone: 'blue' as const },
            { eyebrow: 'Stale', title: 'Sessoes em risco', value: String(summary.stale_sessions_now), tone: 'rose' as const },
            { eyebrow: 'Mesas', title: 'Mesas ao vivo', value: String(summary.active_tables_live), tone: 'amber' as const },
            { eyebrow: 'Fluxo', title: 'Reconnects 30m', value: String(summary.reconnect_events_30m), tone: 'slate' as const },
            { eyebrow: 'Hands', title: 'Hands por minuto', value: String(summary.hands_per_minute), tone: 'slate' as const },
          ]
        : [],
    [summary]
  );

  const seriesCards = useMemo(
    () => [
      {
        key: 'active',
        eyebrow: 'Sessoes',
        title: 'Atividade 5m',
        tone: 'emerald' as const,
        max: getSeriesMax(series, (point) => point.active_sessions),
        getValue: (point: AdminRealtimeSeriesPoint) => point.active_sessions,
      },
      {
        key: 'joins',
        eyebrow: 'Joins',
        title: 'Entradas 5m',
        tone: 'blue' as const,
        max: getSeriesMax(series, (point) => point.joins),
        getValue: (point: AdminRealtimeSeriesPoint) => point.joins,
      },
      {
        key: 'hands',
        eyebrow: 'Hands',
        title: 'Concluidas 5m',
        tone: 'amber' as const,
        max: getSeriesMax(series, (point) => point.hands_finished),
        getValue: (point: AdminRealtimeSeriesPoint) => point.hands_finished,
      },
    ],
    [series]
  );

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Realtime</p>
          <h1>Trafego, heartbeat e operacao ao vivo</h1>
          <p>Monitoramento derivado de sessoes reais de mesa, atividade recente e hands concluidas.</p>
        </div>
        <button type="button" className="admin-secondary-button" onClick={() => void loadData()} disabled={loading}>
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </section>

      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}

      <section className="admin-metrics-grid">
        {metricCards.map((card) => (
          <MetricCard key={card.title} {...card} />
        ))}
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Pulso</p>
            <h2>Serie derivada dos ultimos 60 minutos</h2>
          </div>
          <span className="admin-chip">Gerado em {formatDateTime(payload?.generated_at)}</span>
        </div>

        <div className="admin-series-grid">
          {seriesCards.map((card) => (
            <article key={card.key} className="admin-series-card">
              <p className="admin-section-eyebrow">{card.eyebrow}</p>
              <h3>{card.title}</h3>
              <strong>{series.reduce((acc, point) => acc + card.getValue(point), 0)}</strong>
              <div className="admin-series-chart">
                {series.length ? (
                  series.map((point) => {
                    const value = card.getValue(point);
                    const height = getSeriesHeight(value, card.max);

                    return (
                      <div key={`${card.key}-${point.bucket_start}`} className="admin-series-column" title={`${point.label}: ${value}`}>
                        <div className={`admin-series-bar admin-series-bar--${card.tone}`} style={{ height: `${height}%` }} />
                        <span className="admin-series-label">{point.label}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="admin-note-chip">Sem serie consolidada ainda.</div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Mesas ao vivo</p>
              <h2>Top mesas com atividade agora</h2>
            </div>
            <span className="admin-chip">{payload?.live_tables.length || 0} mesa(s)</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Mesa</th>
                  <th>Club</th>
                  <th>Sessoes</th>
                  <th>Heartbeat</th>
                  <th>Hands 60m</th>
                  <th>Ultima hand</th>
                </tr>
              </thead>
              <tbody>
                {payload?.live_tables.length ? (
                  payload.live_tables.map((table) => (
                    <tr key={table.table_id}>
                      <td>
                        <strong>{table.table_name}</strong>
                        <span>{table.game_type}</span>
                      </td>
                      <td>
                        <strong>{table.club_name}</strong>
                        <span>{table.table_status}</span>
                      </td>
                      <td>
                        <strong>{table.active_sessions}</strong>
                        <span>{table.unique_players} jogador(es)</span>
                      </td>
                      <td>
                        <strong>{table.avg_heartbeat_lag_seconds}s medio</strong>
                        <span>{table.max_heartbeat_lag_seconds}s max</span>
                      </td>
                      <td>{table.hands_last_hour}</td>
                      <td>{formatDateTime(table.last_hand_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>Nenhuma mesa com atividade ao vivo foi encontrada.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Clubs</p>
              <h2>Pressao operacional por club</h2>
            </div>
            <span className="admin-chip">{payload?.club_pressure.length || 0} club(s)</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Sessoes</th>
                  <th>Mesas</th>
                  <th>Reconnect 30m</th>
                  <th>Hands 60m</th>
                </tr>
              </thead>
              <tbody>
                {payload?.club_pressure.length ? (
                  payload.club_pressure.map((club) => (
                    <tr key={club.club_id}>
                      <td>
                        <strong>{club.club_name}</strong>
                        <span>{club.unique_players} jogador(es)</span>
                      </td>
                      <td>
                        <strong>{club.active_sessions}</strong>
                        <span>{club.stale_sessions} stale</span>
                      </td>
                      <td>{club.tables_live}</td>
                      <td>{club.reconnect_events_30m}</td>
                      <td>{club.hands_last_hour}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhum club com pressao operacional neste momento.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Sessoes stale</p>
            <h2>Fila de sessao com heartbeat ruim</h2>
          </div>
          <span className="admin-chip">P95 lag: {summary?.p95_heartbeat_lag_seconds || 0}s</span>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Jogador</th>
                <th>Club</th>
                <th>Mesa</th>
                <th>Device</th>
                <th>Status</th>
                <th>Lag</th>
                <th>Heartbeat</th>
              </tr>
            </thead>
            <tbody>
              {payload?.stale_sessions.length ? (
                payload.stale_sessions.map((session) => (
                  <tr key={session.session_id}>
                    <td>{session.user_name}</td>
                    <td>{session.club_name}</td>
                    <td>{session.table_name}</td>
                    <td>{session.device_id}</td>
                    <td>{session.status}</td>
                    <td>{session.lag_seconds}s</td>
                    <td>{formatDateTime(session.last_heartbeat_at)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>Nenhuma sessao stale foi encontrada agora.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Notas</p>
            <h2>Limites desta etapa</h2>
          </div>
        </div>

        <div className="admin-note-stack">
          {(payload?.notes || []).map((note, index) => (
            <div key={`realtime-note-${index}`} className="admin-note-chip">{note}</div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default RealtimePage;
